/**
 * StyleGuide Luxe — preview des tokens design Bloc 0
 *
 * Pour visualiser : ajouter une route dans App.js, par ex :
 *   <Route path="/style-luxe" element={<StyleGuideLuxe />} />
 * Puis naviguer sur http://localhost:3000/style-luxe
 *
 * Ce composant ne touche aucun autre module.
 */
import React, { useState } from 'react';

const fmtMAD = (v) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 2 }).format(v);

export default function StyleGuideLuxe() {
  const [poids, setPoids] = useState(5);
  const prixOr = 1150;
  const prixVente = 17160;
  const coutHist = 215.26;
  const coutRempl = poids * prixOr;
  const margeReelle = prixVente - coutHist;
  const margeRempl = prixVente - coutRempl;

  return (
    <div className="min-h-screen bg-ivoire-100 text-encre-800 font-sans">
      {/* ── Header ── */}
      <header className="border-b border-ivoire-300 bg-white">
        <div className="max-w-6xl mx-auto px-luxe-md py-luxe-md flex items-center justify-between">
          <div>
            <p className="eyebrow mb-2">Bijouterie · Style Guide</p>
            <h1 className="h-display text-display-md leading-none">Botuha</h1>
          </div>
          <span className="text-xs tracking-luxe uppercase text-encre-400">Bloc 0 · 2026.04.30</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-luxe-md py-luxe-lg space-y-luxe-lg">

        {/* ── Section : Palette ── */}
        <section>
          <p className="eyebrow mb-3">01 · Palette</p>
          <h2 className="h-display text-display-sm mb-luxe">Charte chromatique</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { name: 'Champagne 500', hex: '#C9A961', cls: 'bg-champagne-500' },
              { name: 'Champagne 700', hex: '#8B7355', cls: 'bg-champagne-700' },
              { name: 'Encre 800',     hex: '#0F0F0F', cls: 'bg-encre-800', text: 'text-ivoire-100' },
              { name: 'Ivoire 100',    hex: '#FAF8F3', cls: 'bg-ivoire-100 border border-ivoire-300' },
              { name: 'Ivoire 300',    hex: '#E8E4DC', cls: 'bg-ivoire-300' },
              { name: 'Bordeaux 500',  hex: '#6B1F2E', cls: 'bg-bordeaux-500', text: 'text-ivoire-100' },
              { name: 'Sapin 500',     hex: '#1F3A2E', cls: 'bg-sapin-500',    text: 'text-ivoire-100' },
              { name: 'Champagne 50',  hex: '#FBF7EE', cls: 'bg-champagne-50  border border-ivoire-300' },
              { name: 'Champagne 200', hex: '#EBD9AE', cls: 'bg-champagne-200' },
              { name: 'Encre 400',     hex: '#5A5A5A', cls: 'bg-encre-400',    text: 'text-ivoire-100' },
            ].map((c) => (
              <div key={c.name} className={`${c.cls} ${c.text || ''} rounded-2xl p-5 h-28 flex flex-col justify-between`}>
                <span className="text-xs tracking-luxe uppercase opacity-80">{c.name}</span>
                <span className="font-nums nums text-sm opacity-90">{c.hex}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section : Typographie ── */}
        <section>
          <p className="eyebrow mb-3">02 · Typographie</p>
          <h2 className="h-display text-display-sm mb-luxe">Hierarchie</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="card-luxe p-luxe-md space-y-6">
            <div>
              <span className="eyebrow">Display lg · Cormorant Garamond</span>
              <p className="h-display text-display-lg mt-2">L'art de l'orfevrerie</p>
            </div>
            <div>
              <span className="eyebrow">Display md</span>
              <p className="h-display text-display-md mt-2">Bague solitaire 18K</p>
            </div>
            <div>
              <span className="eyebrow">Display sm</span>
              <p className="h-display text-display-sm mt-2">Collection Botuha</p>
            </div>
            <div>
              <span className="eyebrow">Body Inter 16</span>
              <p className="text-base mt-2 max-w-2xl">
                Une bague alliance en or blanc 18K rhodie, sertie de cinq diamants ronds paves
                de 0.05 carat chacun. Finition haute brillance, taille 54.
              </p>
            </div>
            <div>
              <span className="eyebrow">Eyebrow · UPPERCASE 0.28em tracking</span>
              <p className="text-xs tracking-luxe-wide uppercase font-medium text-champagne-700 mt-2">
                Reference · OF-000067
              </p>
            </div>
          </div>
        </section>

        {/* ── Section : Boutons ── */}
        <section>
          <p className="eyebrow mb-3">03 · Boutons</p>
          <h2 className="h-display text-display-sm mb-luxe">Actions</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="card-luxe p-luxe-md flex flex-wrap items-center gap-4">
            <button className="btn-luxe">Valider la vente</button>
            <button className="btn-luxe-ghost">Annuler</button>
            <button className="btn-luxe" disabled>Indisponible</button>
            <button className="px-5 py-2 rounded-luxe text-xs tracking-luxe uppercase font-medium text-champagne-700 hover:text-champagne-500 transition-colors">
              Lien discret
            </button>
          </div>
        </section>

        {/* ── Section : Inputs ── */}
        <section>
          <p className="eyebrow mb-3">04 · Formulaires</p>
          <h2 className="h-display text-display-sm mb-luxe">Champs</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="card-luxe p-luxe-md grid md:grid-cols-2 gap-luxe">
            <label className="block">
              <span className="eyebrow block mb-2">Reference bijou</span>
              <input className="input-luxe" placeholder="OF-000067-001" />
            </label>
            <label className="block">
              <span className="eyebrow block mb-2">Poids metal (g)</span>
              <input
                type="number"
                step="0.01"
                className="input-luxe nums"
                value={poids}
                onChange={(e) => setPoids(parseFloat(e.target.value) || 0)}
              />
            </label>
          </div>
        </section>

        {/* ── Section : Cartes chiffres ── */}
        <section>
          <p className="eyebrow mb-3">05 · Cartes chiffres · Palier A'</p>
          <h2 className="h-display text-display-sm mb-luxe">Analyse cout bijou</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="grid md:grid-cols-3 gap-luxe">
            <div className="card-luxe p-luxe-md">
              <span className="eyebrow block mb-3">A · Cout historique</span>
              <p className="h-display text-display-md nums">{fmtMAD(coutHist)}</p>
              <p className="mt-2 text-xs tracking-wide text-bordeaux-500">
                ⚠ Donnees pre-existantes douteuses
              </p>
            </div>
            <div className="card-luxe p-luxe-md">
              <span className="eyebrow block mb-3">B · Cout remplacement</span>
              <p className="h-display text-display-md nums">{fmtMAD(coutRempl)}</p>
              <p className="mt-2 text-xs text-encre-400">{poids} g × {prixOr} DH/g</p>
            </div>
            <div className="card-luxe p-luxe-md">
              <span className="eyebrow block mb-3">C · Prix vente</span>
              <p className="h-display text-display-md nums">{fmtMAD(prixVente)}</p>
              <p className="mt-2 text-xs text-encre-400">Affiche en boutique</p>
            </div>
          </div>

          <div className="mt-luxe-md card-luxe p-luxe-md bg-encre-800 text-ivoire-100">
            <span className="eyebrow text-champagne-400 block mb-4">Marges</span>
            <div className="grid md:grid-cols-3 gap-luxe">
              <div>
                <span className="text-xs uppercase tracking-luxe text-ivoire-300">Reelle comptable</span>
                <p className="h-display text-display-sm nums text-champagne-300 mt-1">{fmtMAD(margeReelle)}</p>
              </div>
              <div>
                <span className="text-xs uppercase tracking-luxe text-ivoire-300">Remplacement</span>
                <p className={`h-display text-display-sm nums mt-1 ${margeRempl >= 0 ? 'text-champagne-300' : 'text-bordeaux-500'}`}>
                  {fmtMAD(margeRempl)}
                </p>
              </div>
              <div>
                <span className="text-xs uppercase tracking-luxe text-ivoire-300">Ecart stock dormant</span>
                <p className="h-display text-display-sm nums text-ivoire-100 mt-1">{fmtMAD(coutRempl - coutHist)}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section : Tableau ── */}
        <section>
          <p className="eyebrow mb-3">06 · Tableaux</p>
          <h2 className="h-display text-display-sm mb-luxe">Listing bijoux</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="card-luxe overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ivoire-200">
                <tr>
                  {['Reference', 'Modele', 'Poids', 'Cout reel', 'Prix vente', 'Marge'].map((h) => (
                    <th key={h} className="text-left eyebrow px-5 py-3 font-medium text-encre-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ivoire-300">
                {[
                  { ref: 'OF-000067-001', m: 'Bague solitaire',  p: '5.00 g',  c: 215.26, v: 17160 },
                  { ref: 'OF-000069-002', m: 'Bague zircon',     p: '6.00 g',  c: 1380.93, v: 19003.12 },
                  { ref: 'ALLIA-001OB',   m: 'Alliance pavee',   p: '4.50 g',  c: 215.26, v: 7011 },
                ].map((r) => (
                  <tr key={r.ref} className="hover:bg-champagne-50 transition-colors duration-200">
                    <td className="px-5 py-4 font-medium nums">{r.ref}</td>
                    <td className="px-5 py-4">{r.m}</td>
                    <td className="px-5 py-4 nums">{r.p}</td>
                    <td className="px-5 py-4 nums">{fmtMAD(r.c)}</td>
                    <td className="px-5 py-4 nums">{fmtMAD(r.v)}</td>
                    <td className="px-5 py-4 nums text-champagne-700 font-medium">{fmtMAD(r.v - r.c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Section : Badges et états ── */}
        <section>
          <p className="eyebrow mb-3">07 · Badges</p>
          <h2 className="h-display text-display-sm mb-luxe">Statuts</h2>
          <hr className="luxe-rule mb-luxe-md" />
          <div className="card-luxe p-luxe-md flex flex-wrap gap-3">
            <span className="px-4 py-1.5 rounded-full text-xs tracking-luxe uppercase font-medium bg-champagne-100 text-champagne-800">En boutique</span>
            <span className="px-4 py-1.5 rounded-full text-xs tracking-luxe uppercase font-medium bg-sapin-500 text-ivoire-100">Vendu</span>
            <span className="px-4 py-1.5 rounded-full text-xs tracking-luxe uppercase font-medium bg-bordeaux-500 text-ivoire-100">Retour</span>
            <span className="px-4 py-1.5 rounded-full text-xs tracking-luxe uppercase font-medium bg-ivoire-300 text-encre-600">En atelier</span>
            <span className="px-4 py-1.5 rounded-full text-xs tracking-luxe uppercase font-medium border border-champagne-500 text-champagne-700">Reservation</span>
          </div>
        </section>

        <footer className="pt-luxe-lg pb-luxe-md">
          <hr className="luxe-rule mb-luxe-md" />
          <p className="text-center text-xs tracking-luxe-wide uppercase text-encre-300">
            Bijouterie Botuha · L'excellence par l'orfevrerie
          </p>
        </footer>
      </main>
    </div>
  );
}
